import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';

import {
  Column,
  GenericTableConfiguration,
} from '../generic-table/generic-table.configuration';
import { ValueComputerPipe } from '../generic-table/pipes/value.pipe';

@Component({
  selector: 'app-export-button',
  templateUrl: './export-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportButtonComponent {
  @Input() data: any[] = [];
  @Input()
  set configuration(config: GenericTableConfiguration) {
    if (config) {
      this.columns = this.extractColumns(config);
    }
  }
  @Input() fileName? = 'data';
  @Input() exeptionCellTypes?: Array<string> = ['actions', 'check'];
  @Input() tranlationPath: string;

  private columns: Array<{
    property: string;
    valuePipe: string;
    translation: { enabled: boolean; key?: string; customPath?: string };
  }> = [];

  get isExportEnabled(): boolean {
    return !(!this.columns?.length || !this.data?.length);
  }
  constructor(
    private valueComputerPipe: ValueComputerPipe,
    private translateService: TranslateService
  ) {}

  async onExportClick() {
    const transformedData = await this.transformData(this.data);
    const cols: { property: string; title: string }[] = [];
    this.columns.forEach((col) => {
      if (col.translation && this.tranlationPath) {
        const columnObj = this.translateService.instant(
          this.tranlationPath + col.translation.key
        );
        cols.push({
          property: col.property,
          title: columnObj.TITLE || col.property,
        });
      } else {
        cols.push({ property: col.property, title: col.property });
      }
    });
    this.downloadFile(transformedData, cols, this.fileName);
  }

  private async transformData(data: Array<any>): Promise<any> {
    const transformedData: any[] = [];
    for (const row of data || []) {
      transformedData.push(await this.transformDataRow(row, this.columns));
    }
    return transformedData;
  }

  private async transformDataRow(
    row: any,
    columns: Array<{
      property: string;
      valuePipe: string;
      translation: { enabled: boolean; key?: string; customPath?: string };
    }>
  ): Promise<any> {
    const dataRow = {};
    await columns.forEach((c, i) => {
      if (c.valuePipe) {
        this.valueComputerPipe
          .transform(row[c.property], c.valuePipe, row)
          .pipe(take(1))
          .subscribe((res) => {
            let columnValue;
            if (c.translation.customPath) {
              columnValue = this.translateService.instant(res);
            } else if (c.translation.enabled && this.tranlationPath) {
              columnValue = this.translateService.instant(
                this.tranlationPath + c.translation.key + '.' + res
              );
            } else {
              columnValue = res;
            }
            dataRow[i] = { [c.property]: columnValue };
          });
      } else {
        dataRow[i] = { [c.property]: row[c.property] };
      }
    });
    return dataRow;
  }

  private downloadFile(
    data: Array<any>,
    columns: Array<{ property: string; title: string }>,
    filename: string
  ) {
    const csvData = this.ConvertToCSV(data, columns);
    const blob = new Blob(['\ufeff' + csvData], {
      type: 'text/csv;charset=utf-8;',
    });
    const dwldLink = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const isSafariBrowser =
      navigator.userAgent.indexOf('Safari') !== -1 &&
      navigator.userAgent.indexOf('Chrome') === -1;
    if (isSafariBrowser) {
      // if Safari open in new window to save file with random filename.
      dwldLink.setAttribute('target', '_blank');
    }
    dwldLink.setAttribute('href', url);
    dwldLink.setAttribute('download', filename + '.csv');
    dwldLink.style.visibility = 'hidden';
    document.body.appendChild(dwldLink);
    dwldLink.click();
    document.body.removeChild(dwldLink);
  }

  private ConvertToCSV(
    objArray,
    headerList: { property: string; title: string }[]
  ): string {
    const array =
      typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
    let str = '';
    let row = 'No,';

    for (const index in headerList) {
      row += headerList[index].title + ',';
    }
    row = row.slice(0, -1);
    str += row + '\r\n';
    for (let i = 0; i < array.length; i++) {
      let line = i + 1 + '';
      for (const index in headerList) {
        const head = headerList[index].property;
        line += ',' + array[i][index][head];
      }
      str += line + '\r\n';
    }
    return str;
  }

  private extractColumns(configuration: GenericTableConfiguration): Array<{
    property: string;
    valuePipe: string;
    translation: { enabled: boolean; key?: string };
  }> {
    const columns: Array<{
      property: string;
      valuePipe: string;
      translation: { enabled: boolean; key?: string };
    }> = [];
    (configuration.columnConfigurations.columns || []).forEach(
      (col: Column) => {
        if (!this.exeptionCellTypes.includes(col.cell.type)) {
          columns.push({
            property: col.property,
            valuePipe: col.cell.valuePipe || null,
            translation: col.translation || null,
          });
        }
      }
    );
    return columns;
  }
}
